/*
 * File: FindStory.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:21 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 9:55 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import validator from 'validator';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

// Utilities
import { stripHtml } from '../../../../utilities/utilities';
import { toastMessage, toastSuccess } from '../../../utilities/toaster';

// Services
import { fetchSubmission } from '../../../services/story';

// Components
import {
	Meta,
	AppNavigation,
	Padding,
	Typography,
	TextInput,
	Button,
	LocaleLink,
} from '../../../components';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../constants/base.constants';

// Styles
import * as S from './FindStory.styles';


/**
 * Component
 */

const FindStory = ({ meta: m }) => {

	// Create state handlers
	const [email, setEmail] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get actions from hooks
	const { executeRecaptcha } = useGoogleReCaptcha();

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageFindStory.metaTitle', { partnerName: partner.name }),
		description: t('pageFindStory.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Get legal urls
	const termsUrl = useMemo(() => (partner?.legal?.links?.terms ? partner?.legal?.links?.terms : `https://www.ourwave.org${locale.localePath}/terms`), [partner, locale]);
	const privacyUrl = useMemo(() => (partner?.legal?.links?.privacy ? partner?.legal?.links?.privacy : `https://www.ourwave.org${locale.localePath}/privacy`), [partner, locale]);
	const securityUrl = useMemo(() => (partner?.legal?.links?.security ? partner?.legal?.links?.security : `https://www.ourwave.org${locale.localePath}/security`), [partner, locale]);
	const hasInternalLegalPages = useMemo(() => partner?.legal?.has_internal_legal_pages, [partner]);

	// Handle request story link
	const requestLink = async () => {

		// Validate parameters
		if (!email || !validator.isEmail(email)) {

			// Show error
			toastMessage(uiMode, partner, t('pageFindStory.missingEmailError', { partnerName: partner.name }));

			// Return
			return;
		}

		// Update submission state
		setIsSubmitting(true);

		// Create parameters
		const params = { email };

		// Add reCAPTCHA token
		try { params.recaptchaToken = await executeRecaptcha('request_story_access'); } catch (_) {}

		// Attempt to send secure link
		try {

			// Fetch submission
			await fetchSubmission(params);

			// Update submission state
			setIsSubmitting(false);

			// Clear inputs
			setEmail(null);

			// Show success message
			toastSuccess(uiMode, partner, t('pageFindStory.confirmationMessage'));

		} catch (e) {

			// Update submission state
			setIsSubmitting(false);

			// Show error
			toastMessage(uiMode, partner, t('pageFindStory.submissionError'));
		}
	};

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Component Content */}
			<AppNavigation showFooter>
				<S.Wrapper>

					{/* Animation Panel */}
					<S.AnimationPanel>

						{/* Sky Component */}
						<S.SkyContainer className="step">
							<S.Sky />
						</S.SkyContainer>

						{/* Sun Component */}
						<S.SunContainer className="step">
							<S.SunInnerContainer className="step">
								<S.Sun />
							</S.SunInnerContainer>
						</S.SunContainer>

						{/* Sun Reflection Component */}
						<S.SunReflectionContainer className="step">
							<S.SunReflectionInnerContainer className="step">
								<S.Sun />
							</S.SunReflectionInnerContainer>
						</S.SunReflectionContainer>

						{/* Ocean Reflection */}
						<S.OceanContainer className="step">
							<S.Ocean />
						</S.OceanContainer>

					</S.AnimationPanel>

					{/* Sharing Container */}
					<S.ContainerPanel>
						<Padding left right>
							<S.FormContainer>

								{/* Content Container */}
								<S.ContentContainer>

									{/* Title */}
									<Typography tag="h1" weight="black">
										<Trans t={t} i18nKey="pageFindStory.title">
											Find my story
										</Trans>
									</Typography>

									{/* Subtitle */}
									<Typography tag="p">
										<Trans t={t} i18nKey="pageFindStory.subtitle">
											Looking to update or access your story on
											{' '}
											{{ partnerName: partner.name }}
											? Enter your email here and we&apos;ll send you a secure link.
											{' '}
											<Typography weight="medium">Make sure to use the email you used to submit your story.</Typography>
										</Trans>
									</Typography>

									{/* Instructions */}
									<Typography tag="p" weight="medium" variation="2">
										<Trans t={t} i18nKey="pageFindStory.emailInstructions">
											Haven&apos;t submitted a story yet?
											{' '}
											<LocaleLink to="/share">Click here to share one.</LocaleLink>
										</Trans>
									</Typography>

								</S.ContentContainer>

								{/* Inputs */}
								<S.InputContainer>

									{/* Submission Email */}
									<TextInput
										placeholder={t('pageFindStory.emailPlaceholder')}
										name="message"
										type="text"
										value={email || ''}
										onChange={(event) => {
											const { value } = event.target;
											setEmail(stripHtml(value));
										}}
									/>
								</S.InputContainer>

								{/* Action Container */}
								<S.ActionContainer>
									<Button size="large" onClick={requestLink} isLoading={isSubmitting} disabled={isSubmitting}>
										<Typography>
											<Trans t={t} i18nKey="pageFindStory.sendLink">
												Send secure link
											</Trans>
										</Typography>
										<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
									</Button>
								</S.ActionContainer>

								{/* Legal Container */}
								<S.LegalContainer>
									<Typography tag="p" variation="3">
										<Trans t={t} i18nKey="pageFindStory.agreeTermsPrivacy">
											By requesting a story link from
											{' '}
											{{ partnerName: partner.name }}
											, you agree to our
											{' '}
											<LocaleLink to={termsUrl} target={!hasInternalLegalPages ? '_blank' : null}>Terms of Service</LocaleLink>
											{' '}
											and
											{' '}
											<LocaleLink to={privacyUrl} target={!hasInternalLegalPages ? '_blank' : null}>Privacy Policy</LocaleLink>
											.
										</Trans>
									</Typography>
									<Typography tag="p" variation="3">
										<Trans t={t} i18nKey="pageFindStory.security">
											<LocaleLink to={securityUrl} target={!hasInternalLegalPages ? '_blank' : null}>Learn More</LocaleLink>
											{' '}
											about our security.
										</Trans>
									</Typography>
								</S.LegalContainer>

							</S.FormContainer>
						</Padding>
					</S.ContainerPanel>
				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

FindStory.propTypes = {
	meta: PropTypes.shape()
};
FindStory.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default FindStory;
